import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiHelper } from '../lib/api';

const laborKeys = {
    kind: ['labor-timesheet-kind'],
    current: (year, month) => ['labor-timesheet', 'current', year, month],
    detail: (id) => ['labor-timesheet', 'detail', id],
};

export function useTimesheetKind() {
    return useQuery({
        queryKey: laborKeys.kind,
        queryFn: () => apiHelper.getResource('/timesheet/timesheet-kind/', { suppressToast: true }),
        staleTime: 5 * 60 * 1000,
        retry: false,
    });
}

// Download labor timesheet report (month or range) as Excel.
// GET /timesheet/download/labor/monthly/?year=&month=[&employee_id=]
// GET /timesheet/download/labor/range/?start_year=&start_month=&end_year=&end_month=[&employee_id=]
export function useDownloadLaborTimesheetReport() {
    return useMutation({
        mutationFn: ({ mode, year, month, startYear, startMonth, endYear, endMonth, employeeId, orgId }) => {
            const params = new URLSearchParams();
            let path;
            if (mode === 'range') {
                path = '/timesheet/download/labor/range/';
                params.set('start_year', startYear);
                params.set('start_month', startMonth);
                params.set('end_year', endYear);
                params.set('end_month', endMonth);
            } else {
                path = '/timesheet/download/labor/monthly/';
                if (year != null) params.set('year', year);
                if (month != null) params.set('month', month);
            }
            if (employeeId) params.set('employee_id', employeeId);
            const filename = mode === 'range'
                ? `Labor_Timesheet_${startYear}-${String(startMonth).padStart(2, '0')}_to_${endYear}-${String(endMonth).padStart(2, '0')}.xlsx`
                : `Labor_Timesheet_${year}-${String(month).padStart(2, '0')}.xlsx`;
            return apiHelper.downloadResource(`${path}?${params.toString()}`, filename, { orgId });
        },
        onSuccess: () => toast.success('Timesheet report downloaded.'),
    });
}

export function useLaborTimesheetCurrent(year, month) {
    return useQuery({
        queryKey: laborKeys.current(year, month),
        queryFn: () => {
            const params = {};
            if (year) params.year = year;
            if (month) params.month = month;
            return apiHelper.getResource('/timesheet/labor-timesheets/current/', { params });
        },
        enabled: !!year && !!month,
    });
}

// List sheets visible to the caller — backend scopes this: staff see their own,
// line managers also see their reports', HR sees all. Used to build the
// approvals inbox (filtered client-side by status).
export function useLaborTimesheetsList(enabled = true) {
    return useQuery({
        queryKey: ['labor-timesheet', 'list'],
        queryFn: () => apiHelper.getResource('/timesheet/labor-timesheets/', { suppressToast: true }),
        enabled,
        retry: false,
    });
}

export function useLaborTimesheetById(id) {
    return useQuery({
        queryKey: laborKeys.detail(id),
        queryFn: () => apiHelper.getResource(`/timesheet/labor-timesheets/${id}/`),
        enabled: !!id,
    });
}

export function useLaborTimesheetHours() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, project_row_id, day_index, hours }) =>
            apiHelper.postResource(`/timesheet/labor-timesheets/${id}/hours/`, {
                project_row_id,
                day_index,
                hours,
            }),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

export function useLaborTimesheetDayMeta() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, day_index, leave_code, public_holiday, note }) =>
            apiHelper.postResource(`/timesheet/labor-timesheets/${id}/day-meta/`, {
                day_index,
                ...(leave_code !== undefined && { leave_code }),
                ...(public_holiday !== undefined && { public_holiday }),
                ...(note !== undefined && { note }),
            }),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

export function useLaborTimesheetVariance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, variance_explanation }) =>
            apiHelper.patchResource(`/timesheet/labor-timesheets/${id}/variance/`, {
                variance_explanation,
            }),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

export function useLaborTimesheetCumulative() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, project_row_id, cumulative_loe }) =>
            apiHelper.patchResource(`/timesheet/labor-timesheets/${id}/cumulative/`, {
                project_row_id,
                cumulative_loe,
            }),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

export function useLaborTimesheetEngagementType() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, project_row_id, engagement_type }) =>
            apiHelper.patchResource(`/timesheet/labor-timesheets/${id}/engagement-type/`, {
                project_row_id,
                engagement_type,
            }),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

export function useLaborTimesheetSubmit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) =>
            apiHelper.postResource(`/timesheet/labor-timesheets/${id}/submit/`, {}),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

// Line manager approve (submitted -> approved)
export function useLaborTimesheetApprove() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) =>
            apiHelper.postResource(`/timesheet/labor-timesheets/${id}/approve/`, {}),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

// HR approve (approved -> hr_approved)
export function useLaborTimesheetHrApprove() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) =>
            apiHelper.postResource(`/timesheet/labor-timesheets/${id}/hr-approve/`, {}),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}

// Line manager: reject submitted. HR: reject submitted or approved.
export function useLaborTimesheetReject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, reason }) =>
            apiHelper.postResource(`/timesheet/labor-timesheets/${id}/reject/`, { reason }),
        onSuccess: (data) => {
            queryClient.setQueryData(laborKeys.current(data.period?.year, data.period?.month), data);
        },
    });
}
